import { useRef, useState, useEffect } from 'react';
import { caseStudies } from '@/mocks/casesData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { visible, ref };
}

export default function WanglaojiCase() {
  const data = caseStudies[2];
  const heroRef = useVisible(0.05);
  const challengeRef = useVisible(0.1);
  const solutionRef = useVisible(0.1);
  const resultsRef = useVisible(0.1);

  const flowSteps = [
    { label: '品牌方', desc: '费用精准投放' },
    { label: '经销商', desc: '数字化管控，截留可见' },
    { label: '二批商', desc: '费用穿透，利益可量化' },
    { label: '终端门店', desc: '扫码即得奖励，动销有动力' },
    { label: '消费者', desc: '扫码领红包，购买有惊喜' },
  ];

  const activities = [
    {
      icon: 'ri-wechat-line',
      title: '微信红包bC联动',
      desc: '终端门店进货扫码领奖，消费者购买扫码领红包，双端激励同步触发',
    },
    {
      icon: 'ri-coupon-line',
      title: '一元乐享bC联动',
      desc: '低门槛高频次的消费者互动活动，快速激活终端动销氛围，提升消费者复购意愿',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Case Header with Banner */}
        <div ref={heroRef.ref} className={`mb-10 md:mb-14 transition-all duration-700 ${heroRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative rounded-2xl overflow-hidden h-48 md:h-72 mb-8">
            <img
              src={data.heroImage}
              alt={`${data.brand}案例背景`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-red-900/80 via-red-800/50 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{data.brand}</h2>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">{data.subtitle}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-text-muted">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface rounded-lg">
              <i className="ri-building-line" /> 行业：{data.industry}
            </span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface rounded-lg">
              <i className="ri-service-line" /> 服务：{data.service}
            </span>
          </div>
        </div>

        {/* Challenge */}
        <div ref={challengeRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${challengeRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-red-600 rounded-lg">
              <i className="ri-error-warning-line text-white text-sm" />
            </span>
            核心挑战
          </h3>
          <div className="bg-surface rounded-xl p-5 md:p-7 border border-gray-100">
            <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-4">{data.challengeIntro}</p>
            <ul className="space-y-2.5">
              {data.challengePoints.map((item, i) => (
                <li key={i} className="text-sm text-text-secondary flex items-start gap-2.5">
                  <i className="ri-arrow-right-s-line text-red-600 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Solution */}
        <div ref={solutionRef.ref} className={`mb-10 md:mb-14 transition-all duration-500 ${solutionRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-accent rounded-lg">
              <i className="ri-lightbulb-line text-primary text-sm" />
            </span>
            解决方案
          </h3>

          <div className="bg-primary rounded-xl p-5 md:p-7 text-white mb-6">
            <p className="text-sm md:text-base leading-relaxed">
              王老吉携手融慧数科，从零构建专属<strong className="text-accent">物码营销数字化链路</strong>，以"微信红包bC联动"与"一元乐享bC联动"为核心活动形式，在不同产品线上分批试点，搭建物码营销系统+SFA一体化解决方案，系统性改造并疏通全链路费用传递通道。
            </p>
          </div>

          {/* Flow Diagram */}
          <div className="bg-surface rounded-xl p-5 md:p-7 border border-gray-100 mb-6">
            <h4 className="text-sm font-semibold text-primary mb-5">链路改造核心逻辑</h4>
            <div className="flex flex-col md:flex-row items-stretch gap-3">
              {flowSteps.map((step, i) => (
                <div key={i} className="flex-1 flex flex-col items-center text-center relative">
                  <div className="w-full bg-white rounded-xl p-4 border border-gray-100 hover:border-red-200 hover:shadow-md transition-all duration-300">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-2">
                      <span className="text-xs font-bold text-red-600">{i + 1}</span>
                    </div>
                    <h5 className="text-xs font-semibold text-primary mb-1">{step.label}</h5>
                    <p className="text-xs text-text-secondary">{step.desc}</p>
                  </div>
                  {i < flowSteps.length - 1 && (
                    <>
                      <div className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10">
                        <i className="ri-arrow-down-s-line md:ri-arrow-right-s-line text-red-400 text-lg" />
                      </div>
                      <div className="flex md:hidden py-2">
                        <i className="ri-arrow-down-s-line text-red-400 text-lg" />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Activities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activities.map((act, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-red-200 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-lg mb-3 group-hover:bg-red-600 transition-colors">
                  <i className={`${act.icon} text-red-600 text-lg group-hover:text-white transition-colors`} />
                </div>
                <h4 className="text-sm font-semibold text-primary mb-2">{act.title}</h4>
                <p className="text-xs text-text-secondary leading-relaxed">{act.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div ref={resultsRef.ref} className={`transition-all duration-500 ${resultsRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h3 className="text-lg md:text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-green-600 rounded-lg">
              <i className="ri-trophy-line text-white text-sm" />
            </span>
            落地成果
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.results.map((result, i) => (
              <div
                key={i}
                className="bg-surface rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-300 flex items-start gap-3"
              >
                <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-xs" />
                </div>
                <p className="text-sm text-text-secondary leading-relaxed">{result}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}